import { inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { EventTypes } from 'src/app/common/shared/observer.enum';
import { Observer } from 'src/app/common/shared/observer.interface';
import { FieldAttributeConstructorService } from 'src/app/forms/fields/field-attributes-constructor.service';
import { ContactUsFormTypes, FormTypes } from 'src/app/forms/forms.enum';
import { AddFeedbackRequest } from 'src/app/swagger-api';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import { FormsAttributeService } from '../../forms/forms-attribute.service';
import { ContactUsSubject } from './contact-us-subject.model';
import { contactUsMetadata } from './contact-us.mock';

@Injectable()
export class ContactUsService implements OnDestroy {
  private subject: ContactUsSubject = new ContactUsSubject();

  private apiService = inject(RestApiService);
  private formsAttributeService = inject(FormsAttributeService);
  private fieldsService = inject(FieldAttributeConstructorService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  public constructor() {
    this.sessionService.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.fetchFormMetadata();
    });
  }

  public ngOnDestroy(): void {
    this.subject.destroy();
  }

  /**
   * @description Subscribes on subject changes and calls fetchFormMetadata() method.
   * @param { Observer } observer
   */
  public subscribeOnChanges(observer: Observer): void {
    this.subject.attach(observer);
    this.fetchFormMetadata();
  }

  /**
   * @description Unsubscribes from subject changes.
   * @param { Observer } observer
   */
  public unsubscribeFromChanges(observer: Observer): void {
    this.subject.detach(observer);
  }

  /**
   * @description Calls restSocialFeedbackPost() method and processes the result.
   * @param { object } values
   */
  public sendMessage(request: AddFeedbackRequest): void {
    this.apiService
      .restSocialFeedbackPost(request)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl('/'));
      });
  }

  // Now, there is only one contact us form available (for logged in users)
  // This should not be shown to logged out ones.
  private fetchFormMetadata(): void {
    this.subject.notify(EventTypes.UPDATE, null);

    const data: AddFeedbackRequest = { subject: '', message: '' };

    this.subject.notify(EventTypes.UPDATE, {
      formAttribute: this.formsAttributeService.getFormAttribute(FormTypes.CONTACT_US),
      fieldAttributes: this.fieldsService.convertAttributesMetadata(
        contactUsMetadata[ContactUsFormTypes.CONTACT_US_LOGGED_IN](),
        FormTypes.CONTACT_US,
      ),
      data,
    });
  }
}
